import React, { createContext, useContext, useState, useCallback } from "react";
import { auth, db, storage } from "../firebase"; 
import {
  ref,
  deleteObject,
  listAll,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  runTransaction,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { toastError, toastSuccess } from "../helpers/toast";
import { sendMail } from "../helpers/email"; 
import { GlobalStateContext } from "./GlobalStateContext";

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const GlobalState = useContext(GlobalStateContext);

  const getAuthErrorMessage = (code) => {
    switch (code) {
      case "auth/email-already-in-use":
        return "This email is already registered. Use a different email or log in.";
      case "auth/invalid-email":
        return "The email address is invalid. Please check and try again.";
      case "auth/weak-password":
        return "Password is too weak. Use at least 6 characters.";
      case "auth/operation-not-allowed":
        return "Email/password sign-in is disabled. Contact support.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a moment and try again.";
      default:
        return `Account creation failed: ${code}`;
    }
  };

  const createUser = async (dbName, data, setLoading, role, attachment = null) => {
    setLoading(true);
    try {
      const { password, ...dataWithoutPassword } = data;
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data?.email,
        password
      );

      await updateProfile(userCredential.user, {
        displayName: role,
      });

      await sendEmailVerification(userCredential.user);

      const ref = doc(db, dbName, userCredential.user.uid);
      await setDoc(ref, {
        ...dataWithoutPassword,
        uid: userCredential.user.uid,
        isVerified: false,
      })
        .then(() => {
          toastSuccess(`${dbName} Created Successfully`);
          setLoading(false);
          sendMail(data.email, "registered", password);
        })
        .catch((error) => {
          console.error("Error adding document: ", error);
          setLoading(false);
        });
    } catch (error) {
      const friendlyMessage = error.code
        ? getAuthErrorMessage(error.code)
        : error.message;
      console.error("createUser error:", error.code, error.message);
      toastError(friendlyMessage);
      setError(friendlyMessage);
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setIsAuthenticated(true);

      try {
        const { recordLoginEvent } = await import("../helpers/loginHistory");
        await recordLoginEvent(userCredential.user, true);
      } catch (error) {
        console.error("Failed to record login event:", error);
      }

      // Replace this exact block inside signIn:
      if (
        userCredential.user.displayName !== "admin" &&
        userCredential.user.displayName !== "moderator" &&
        email !== "bmwda07@gmail.com" // Extra safety check just in case displayName is missing
      ) {
        try {
          const userData = await getCurrentUserData(
            userCredential.user.uid,
            userCredential.user.displayName
          );
          setUserProfile({ ...userData, id: userCredential.user.uid });

          // Auto-sync email verification from Firebase Auth → Firestore
          if (userCredential.user.emailVerified && userData && !userData.isVerified) {
            const userDocRef = doc(db, "new_users", userCredential.user.uid);
            await updateDoc(userDocRef, { isVerified: true });
          }
        } catch (error) {
          console.error("Failed to get user data:", error);
        }
      }
      setCurrentUser(userCredential.user);
      if (userCredential.user.displayName === "moderator") {
        const { assignedRegion } = await getDocument(
          "Moderator",
          userCredential.user.uid
        );
        sessionStorage.setItem(
          "user",
          JSON.stringify({ ...userCredential.user, assignedRegion })
        );
      } else {
        sessionStorage.setItem("user", JSON.stringify(userCredential.user));
      }
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      try {
        const { recordLoginEvent } = await import("../helpers/loginHistory");
        await recordLoginEvent({ email }, false);
      } catch (recordError) {
        console.error("Failed to record login failure:", recordError);
      }
      return null;
    }
  };

  const signOut = async () => {
    try {
      await auth.signOut();
      setIsAuthenticated(false);
      toastSuccess("Successfully signed out");
      sessionStorage.removeItem("user");
      setCurrentUser(null);
    } catch (error) {
      toastError(error.message);
      setError(error.message);
    }
  };

  const getDocument = useCallback(async (collection, uid) => {
    try {
      if (!uid) return null;
      const dbRef = doc(db, collection, uid);
      const docSnap = await getDoc(dbRef);
      if (docSnap.exists()) return docSnap.data();
    } catch (error) {
      toastError(error.message);
      return null;
    }
    return null;
  }, []);

  const getCurrentUserData = useCallback(
    async (uid, role) => {
      try {
        // 1. Completely skip the database search if the role is admin
        if (role === "admin") {
          return { role: "admin", id: uid };
        }

        let userData = await getDocument("Employee", uid);
        if (!userData) userData = await getDocument("new_users", uid);
        if (!userData) userData = await getDocument("Moderator", uid);
        
        // ---> NEW: Check fieldAgents collection <---
        if (!userData) userData = await getDocument("fieldAgents", uid);

        if (userData) return userData;
        
        // 2. Only throw the error if a role exists AND it is not admin
        else if (role && role !== "admin") {
          toastError("No such document!");
        }
        
        return userData;
      } catch (error) {
        toastError(error.message);
        return null;
      }
    },
    [getDocument]
  );

  const storeData = async (dbName, data, setLoading) => {
    try {
      setLoading(true);
      data = { ...data, created_at: serverTimestamp() };
      const response = await addDoc(collection(db, dbName), data);
      toastSuccess(`${dbName} Created Successfully`);
      setLoading(false);
      return response.id;
    } catch (error) {
      toastError(error.message);
      setLoading(false);
    }
  };

  const storeDataAtKey = async (collection, key, data, setLoading) => {
    try {
      setLoading(true);
      const docRef = doc(db, collection, key);
      await setDoc(docRef, data);
      toastSuccess(`${collection} Created Successfully`);
      setLoading(false);
    } catch (error) {
      toastError(error.message);
      setLoading(false);
    }
  };

  const storeObjectAtKey = async (collection, key, subkey, data, setLoading) => {
    try {
      setLoading(true);
      const docRef = doc(db, collection, key.toLowerCase());
      await setDoc(docRef, { [subkey]: data }, { merge: true });
      toastSuccess(`${collection} Created Successfully`);
      setLoading(false);
    } catch (error) {
      toastError(error.message);
      setLoading(false);
    }
  };

  const addCity = async (city, cities, setCities) => {
    try {
      const cityName = city.trim().toLowerCase();
      const cityRef = doc(db, "Cities", city.trim().toLowerCase());
      await setDoc(cityRef, { facilityCount: increment(1) }, { merge: true });
      if (!Object.keys(cities).includes(cityName))
        setCities({ ...cities, [cityName]: cities[cityName] + 1 });
    } catch (error) {
      toastError(error.message);
    }
  };

  const getCities = async (setLoading, setCities = null) => {
    setLoading(true);
    try {
      const citiesCollectionRef = collection(db, "Cities");
      const querySnapshot = await getDocs(citiesCollectionRef);
      const cities = {};
      querySnapshot.forEach((doc) => { cities[doc.id] = doc.data(); });
      setLoading(false);
      setCities && setCities(cities);
      return cities;
    } catch (error) {
      setLoading(false);
      toastError(error.message);
    }
  };

  const getFacilityData = async (setFacilityData, setLoading) => {
    try {
      setLoading(true);
      const facilityRef = collection(db, "Facilities");
      const snapshot = await getDocs(facilityRef);
      const facilities = [];
      const cities = [];
      snapshot.forEach((doc) => {
        cities.push(doc.id);
        const cityData = doc.data();
        Object.values(cityData).forEach((facility) => facilities.push(facility));
      });
      setFacilityData(facilities);
      setLoading(false);
      return cities;
    } catch (error) {
      toastError(error.message || "Error fetching facilities");
      setLoading(false);
    }
  };

  const getData = useCallback(async (collectionName, setloading, setData) => {
    setloading(true);
    try {
      const data = [];
      const querySnapshot = await getDocs(collection(db, collectionName));
      querySnapshot.forEach((doc) => { data.push({ id: doc.id, ...doc.data() }); });
      setData(data);
      setloading(false);
    } catch (error) {
      toastError(error.message);
      setError(error.message);
    }
  }, []);

  const getServerData = async (dbName, setloading, setData, page, pageSize, lastVisible) => {
    setloading(true);
    try {
      const data = [];
      let q = query(collection(db, dbName), orderBy("id"), limit(pageSize));
      if (page !== 1) {
        q = query(collection(db, dbName), orderBy("id"), startAfter(lastVisible), limit(pageSize));
      }
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => { data.push({ id: doc.id, ...doc.data() }); });
      setloading(false);
    } catch (error) {
      toastError(error.message);
      setError(error.message);
      setloading(false);
    }
  };

  const updateData = async (docId, dbName, updatedData, setLoading) => {
    try {
      setLoading(true);
      const docRef = doc(db, dbName, docId);
      await runTransaction(db, async (transaction) => {
        const docSnapshot = await transaction.get(docRef);
        if (!docSnapshot.exists()) throw new Error("Document does not exist!");
        const { id, ...dataWithoutId } = updatedData;
        transaction.update(docRef, dataWithoutId);
      });
      toastSuccess(`${dbName} Updated Successfully`);
    } catch (error) {
      toastError(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteObjectWithKey = async (collection, key, subkey, setloading) => {
    try {
      setloading(true);
      const docRef = doc(db, collection, key.toLowerCase());
      await updateDoc(docRef, { [subkey]: deleteField() });
      setloading(false);
    } catch (error) {
      toastError(error.message);
      setloading(false);
    }
  };

  const decrementCityCount = async (city) => {
    try {
      const cityRef = doc(db, "Cities", city.trim().toLowerCase());
      await setDoc(cityRef, { facilityCount: increment(-1) }, { merge: true });
    } catch (error) {
      setError(error.message);
    }
  };

  const recordAttendance = async (empId, role, action) => {
    const attendanceRef = doc(db, "EmpAttendance", empId);
    const currentDate = new Date().toISOString().split("T")[0];
    if (role === "Employee") {
      const docSnap = await getDoc(attendanceRef);
      if (action === "start") {
        if (docSnap.exists() && docSnap.data()[currentDate] && docSnap.data()[currentDate].startTime) {
          toastError("You have already started your attendance for today.");
          return;
        }
        await setDoc(attendanceRef, { [currentDate]: { startTime: serverTimestamp() } }, { merge: true });
        toastSuccess("Attendance started successfully.");
      } else if (action === "end") {
        if (docSnap.exists() && docSnap.data()[currentDate] && docSnap.data()[currentDate].endTime) {
          toastError("You have already ended your attendance for today.");
          return;
        }
        await updateDoc(attendanceRef, { [`${currentDate}.endTime`]: serverTimestamp() });
        toastSuccess("Attendance ended successfully.");
      }
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      if (user) {
        const userEmail = user.email?.toLowerCase() || "";
        const isAdminAccount = user.displayName === "admin" || userEmail === "bmwda07@gmail.com";
        const userData = isAdminAccount ? { role: "admin", id: user.uid } : await getCurrentUserData(user.uid, user.displayName);
        if (isMounted) {
          setUserProfile(userData);
          setCurrentUser(user);
          setLoading(false);
        }
      } else {
        if (isMounted) {
          setUserProfile(null);
          setCurrentUser(null);
          setLoading(false);
        }
      }
    });
    return () => { isMounted = false; unsubscribe(); };
  }, []);

  const VerifyAccount = async (docId, dbName, setloading) => {
    try {
      setloading(true);
      const docRef = doc(db, dbName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.data()?.isVerified) {
        toastError("Account is already verified");
      } else {
        await updateDoc(docRef, { isVerified: true });
        toastSuccess("Account Verified Successfully");
      }
      setloading(false);
    } catch (error) {
      toastError(error.message);
      setloading(false);
    }
  };

  const deleteData = async (docId, dbName, setloading) => {
    try {
      setloading(true);
      const docRef = doc(db, dbName, docId);
      await deleteDoc(docRef);
      toastSuccess("Document deleted successfully");
      setloading(false);
    } catch (error) {
      toastError(error.message);
      setloading(false);
    }
  };

  const deleteUserData = async (userId, collection, setLoading) => {
    try {
      setLoading(true);
      const docRef = doc(db, collection, userId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error("User document does not exist");

      try {
        const storageRef = ref(storage, `${collection}/${userId}`);
        const listResult = await listAll(storageRef);
        const deletePromises = listResult.items.map((itemRef) => deleteObject(itemRef));
        await Promise.all(deletePromises);
        toastSuccess("User images deleted successfully");
      } catch (error) {
        console.log("No images to delete or error deleting images:", error);
      }
      await deleteDoc(docRef);
      toastSuccess("User deleted successfully");
    } catch (error) {
      console.error("Error deleting user data:", error);
      toastError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (collection, userId, file, setLoading) => {
    setLoading(true);
    try {
      const storageRef = ref(storage, `${collection}/${userId}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        (snapshot) => {},
        (error) => { toastError(error.message); setLoading(false); },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const docref = doc(db, "new_users", userId);
          await updateDoc(docref, { images: arrayUnion(downloadURL) });
          toastSuccess("File uploaded successfully");
          setLoading(false);
          return downloadURL;
        }
      );
    } catch (error) {
      toastError(error.message);
      setLoading(false);
    }
  };

  const value = {
    currentUser, error, createUser, signIn, signOut, loading, isAuthenticated, userProfile,
    storeData, storeDataAtKey, getData, addCity, getCities, getFacilityData, updateData,
    deleteData, decrementCityCount, deleteObjectWithKey, recordAttendance, getDocument,
    getServerData, VerifyAccount, storeObjectAtKey, deleteUserData, handleFileUpload,
  };
  return <AdminContext.Provider value={value}>{!loading && children}</AdminContext.Provider>;
};